import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch } from "@phosphor-icons/react";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";
import { formatPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { RelationshipStageBadge } from "@/components/prospects/shared";
import { HUB_TYPE_LABELS, HUB_INDUSTRY_LABELS, HUB_TYPE_INDUSTRY } from "@/lib/hubEnums";

function payload(form) {
  const out = {};
  Object.entries(form).forEach(([k, v]) => { out[k] = v === "" ? null : v; });
  out.organization_name = form.organization_name;
  out.lifecycle_status = form.lifecycle_status;
  out.status = form.status;
  return out;
}

export default function EditProspectDialog({ open, prospect, sources, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isAdmin } = useAuth();
  const { dirty } = useUnsavedGuard([form], form !== null);

  // Outside clicks never close this dialog; X / Escape ask before discarding edits.
  const attemptClose = () => {
    if (dirty && !window.confirm("You have unsaved changes. Discard them?")) return;
    onClose();
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && prospect) {
      setForm({
        organization_name: prospect.organization_name || "",
        lead_source_id: prospect.lead_source_id || "",
        lead_source_detail: prospect.lead_source_detail || "",
        lifecycle_status: prospect.lifecycle_status || "prospect",
        relationship_stage: ["cold", "warm", "hot"].includes(prospect.relationship_stage) ? prospect.relationship_stage : "",
        date_entered: prospect.date_entered || "",
        website: prospect.website || "",
        linkedin_url: prospect.linkedin_url || "",
        general_office_phone: formatPhone(prospect.general_office_phone) || "",
        general_email: prospect.general_email || "",
        hq_street_address: prospect.hq_street_address || "",
        hq_city: prospect.hq_city || "",
        hq_state: prospect.hq_state || "",
        hq_zip: prospect.hq_zip || "",
        notes: prospect.notes || "",
        ...(isAdmin ? { internal_notes: prospect.internal_notes || "" } : {}),
        hub_customer_type: prospect.hub_customer_type || "",
        hub_customer_industry: prospect.hub_customer_industry || "",
        status: prospect.status || "active",
      });
    }
  }, [open, prospect, setForm]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/prospects/${prospect.id}`, payload(form));
      toast.success("Prospect updated");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!form) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && attemptClose()}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => { e.preventDefault(); attemptClose(); }}
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-sm sm:max-w-3xl"
        data-testid="edit-prospect-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Edit Prospect</DialogTitle>
          <DialogDescription>Ownership and Lead Originator are managed separately and never edited here.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Organization name *</Label>
            <Input required value={form.organization_name} onChange={set("organization_name")} className="rounded-sm" data-testid="edit-org-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Lead source</Label>
              <Select value={form.lead_source_id} onValueChange={setVal("lead_source_id")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(sources || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Lead source detail</Label>
              <Input value={form.lead_source_detail} onChange={set("lead_source_detail")} className="rounded-sm" placeholder='e.g. "Referred by ABC Property Management"' />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Organization Classification</Label>
              <Select value={form.lifecycle_status} onValueChange={setVal("lifecycle_status")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-lifecycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6D6D70]">
                Prospect or Client classification. For prospects, choose a Lifecycle Stage below. Client is shown in TFS green.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Date entered CRM</Label>
              <Input type="date" value={form.date_entered} onChange={set("date_entered")} className="rounded-sm" data-testid="edit-date-entered" />
            </div>
          </div>
          {form.lifecycle_status !== "client" ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Lifecycle Stage</Label>
                <RelationshipStageBadge stage={form.relationship_stage} testId="edit-stage-current" />
              </div>
              {!form.relationship_stage && (
                <p className="text-xs font-semibold text-[#B45309]" data-testid="stage-needs-review-note">
                  Needs review: Lifecycle Stage is not set. Select Cold, Warm, or Hot based on documented relationship evidence.
                </p>
              )}
              <Select value={form.relationship_stage} onValueChange={setVal("relationship_stage")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-relationship-stage">
                  <SelectValue placeholder="Needs review — select an evidence-based stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cold">Cold — identified potential customer; before meaningful back-and-forth communication</SelectItem>
                  <SelectItem value="warm">Warm — the prospect is communicating back and forth with TFS</SelectItem>
                  <SelectItem value="hot">Hot — a specific pursuit is in proposal or negotiation</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6D6D70]">
                Set from documented evidence — stages are never advanced automatically. Clients display TFS green from Organization Classification.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="derived-client-stage">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Lifecycle Stage</Label>
              <div className="flex items-center gap-2 rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2">
                <RelationshipStageBadge lifecycle="client" testId="edit-stage-client-derived" />
                <span className="text-xs text-[#6D6D70]">Derived from Organization Classification = Client — not stored as a separate stage.</span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Admin Hub Customer Type</Label>
              <Select value={form.hub_customer_type}
                onValueChange={(v) => setForm((f) => ({ ...f, hub_customer_type: v, hub_customer_industry: "" }))}>
                <SelectTrigger className="rounded-sm" data-testid="edit-hub-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(HUB_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Admin Hub Industry</Label>
              <Select value={form.hub_customer_industry} onValueChange={setVal("hub_customer_industry")}
                disabled={!form.hub_customer_type}>
                <SelectTrigger className="rounded-sm" data-testid="edit-hub-industry"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {form.hub_customer_type && (
                    <SelectItem value={HUB_TYPE_INDUSTRY[form.hub_customer_type]}>
                      {HUB_INDUSTRY_LABELS[HUB_TYPE_INDUSTRY[form.hub_customer_type]]}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6D6D70]">
                Customer-level classification used by the Admin Hub transfer — not the awarded service. Only the Industry compatible with the selected Type is offered.
              </p>
              {form.hub_customer_type && form.hub_customer_industry &&
                HUB_TYPE_INDUSTRY[form.hub_customer_type] !== form.hub_customer_industry && (
                <p className="text-xs font-semibold text-[#B91C1C]" data-testid="hub-pair-mismatch-note">
                  Incompatible pair: {HUB_TYPE_LABELS[form.hub_customer_type]} must pair with {HUB_INDUSTRY_LABELS[HUB_TYPE_INDUSTRY[form.hub_customer_type]]}.
                  Correct it here — saving, opportunity edits, and the Admin Hub transfer are blocked until fixed.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Website</Label>
              <Input value={form.website} onChange={set("website")} className="rounded-sm" placeholder="https://..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">LinkedIn company page</Label>
              <Input value={form.linkedin_url} onChange={set("linkedin_url")} className="rounded-sm" placeholder="https://linkedin.com/company/..." />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">General office phone</Label>
              <Input value={form.general_office_phone} onChange={set("general_office_phone")} onBlur={(e) => setVal("general_office_phone")(formatPhone(e.target.value))} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">General email</Label>
              <Input type="email" value={form.general_email} onChange={set("general_email")} className="rounded-sm" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Headquarters Address</Label>
            <p className="text-[11px] text-[#6D6D70]">Organization-level HQ — independent from Service Location records. Used by the city/market search.</p>
            <Input value={form.hq_street_address} onChange={set("hq_street_address")} placeholder="Street address" className="rounded-sm" data-testid="edit-hq-street" />
            <div className="grid grid-cols-3 gap-3">
              <Input value={form.hq_city} onChange={set("hq_city")} placeholder="City" className="rounded-sm" data-testid="edit-hq-city" />
              <Input value={form.hq_state} onChange={set("hq_state")} placeholder="State" className="rounded-sm" data-testid="edit-hq-state" />
              <Input value={form.hq_zip} onChange={set("hq_zip")} placeholder="ZIP" className="rounded-sm" data-testid="edit-hq-zip" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={3} className="rounded-sm" data-testid="edit-notes" />
          </div>
          {isAdmin && (
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#B45309]">Internal notes (administrators only)</Label>
              <Textarea value={form.internal_notes || ""} onChange={set("internal_notes")} rows={3} className="rounded-sm border-[#F59E0B]/40" data-testid="edit-internal-notes" />
              <p className="text-[11px] text-[#6D6D70]">Never visible to salespeople. Included as internal notes on the Admin Hub transfer.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Record status</Label>
            <Select value={form.status} onValueChange={setVal("status")}>
              <SelectTrigger className="rounded-sm" data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-edit-prospect">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
